import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPut } from "../../lib/api";

type Branch = { id: string; name: string };
type Staff = {
  id: string;
  branch_id: string;
  full_name: string;
  skill_tags: string[];
  is_active: boolean;
};

type StaffForm = {
  branch_id: string;
  full_name: string;
  skill_tags_csv: string;
  is_active: boolean;
};

function emptyStaffForm(branchId = ""): StaffForm {
  return { branch_id: branchId, full_name: "", skill_tags_csv: "cuts,color", is_active: true };
}

function formToPayload(form: StaffForm) {
  return {
    branch_id: form.branch_id,
    full_name: form.full_name,
    is_active: form.is_active,
    skill_tags: form.skill_tags_csv
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean),
  };
}

export function TgStaffPage() {
  const qc = useQueryClient();
  const [branchFilter, setBranchFilter] = useState("");
  const [createForm, setCreateForm] = useState<StaffForm>(emptyStaffForm());
  const [editingId, setEditingId] = useState("");
  const [editForm, setEditForm] = useState<StaffForm>(emptyStaffForm());
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const branches = useQuery<{ items: Branch[] }>({
    queryKey: ["tg-staff-branches"],
    queryFn: () => apiGet("/v3/branches?size=100"),
    staleTime: 60_000,
  });
  const staff = useQuery<{ items: Staff[] }>({
    queryKey: ["tg-staff-list"],
    queryFn: () => apiGet("/v3/staff?size=200"),
  });

  const filtered = useMemo(
    () => (staff.data?.items ?? []).filter((row) => !branchFilter || row.branch_id === branchFilter),
    [staff.data?.items, branchFilter]
  );
  const branchNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of branches.data?.items ?? []) map.set(b.id, b.name);
    return map;
  }, [branches.data?.items]);

  const createMutation = useMutation({
    mutationFn: () => apiPost("/v3/staff", formToPayload(createForm)),
    onSuccess: async () => {
      setNotice("Staff member added");
      setError("");
      setCreateForm((prev) => emptyStaffForm(prev.branch_id));
      await qc.invalidateQueries({ queryKey: ["tg-staff-list"] });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Create failed");
      setNotice("");
    },
  });
  const updateMutation = useMutation({
    mutationFn: () => apiPut(`/v3/staff/${editingId}`, formToPayload(editForm)),
    onSuccess: async () => {
      setNotice("Staff member updated");
      setError("");
      setEditingId("");
      await qc.invalidateQueries({ queryKey: ["tg-staff-list"] });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Update failed");
      setNotice("");
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/v3/staff/${id}`),
    onSuccess: async () => {
      setNotice("Staff member deleted");
      setError("");
      if (editingId) setEditingId("");
      await qc.invalidateQueries({ queryKey: ["tg-staff-list"] });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Delete failed");
      setNotice("");
    },
  });

  const startEdit = (row: Staff) => {
    setEditingId(row.id);
    setEditForm({
      branch_id: row.branch_id,
      full_name: row.full_name,
      skill_tags_csv: row.skill_tags.join(","),
      is_active: row.is_active,
    });
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-bold text-gray-900">Staff</h1>
        <span className="text-xs text-gray-500">{filtered.length} shown</span>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-3">
        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Branch filter</label>
        <select
          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          value={branchFilter}
          onChange={(e) => setBranchFilter(e.target.value)}
        >
          <option value="">All branches</option>
          {(branches.data?.items ?? []).map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-3 space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Quick add</p>
        <StaffFormFields form={createForm} branches={branches.data?.items ?? []} onChange={setCreateForm} />
        <button
          className="w-full rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
          disabled={!createForm.branch_id || !createForm.full_name.trim() || createMutation.isPending}
          onClick={() => createMutation.mutate()}
        >
          {createMutation.isPending ? "Adding..." : "Add staff"}
        </button>
      </div>

      {notice && <div className="rounded-lg bg-emerald-50 p-2 text-sm text-emerald-700">{notice}</div>}
      {error && <div className="rounded-lg bg-rose-50 p-2 text-sm text-rose-700">{error}</div>}

      {staff.isLoading && <div className="text-center text-sm text-gray-500">Loading staff...</div>}

      <div className="space-y-2">
        {filtered.map((row) => {
          const isEditing = row.id === editingId;
          return (
            <div key={row.id} className="rounded-xl border border-gray-200 bg-white p-3">
              {isEditing ? (
                <div className="space-y-2">
                  <StaffFormFields form={editForm} branches={branches.data?.items ?? []} onChange={setEditForm} />
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                    />
                    Active
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      className="rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                      disabled={!editForm.branch_id || !editForm.full_name.trim() || updateMutation.isPending}
                      onClick={() => updateMutation.mutate()}
                    >
                      Save
                    </button>
                    <button className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700" onClick={() => setEditingId("")}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{row.full_name}</p>
                      <p className="text-xs text-gray-500">{branchNameMap.get(row.branch_id) || "Unknown branch"}</p>
                      <p className="mt-1 text-xs text-gray-600">{row.skill_tags.join(", ") || "No skills"}</p>
                    </div>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                        row.is_active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {row.is_active ? "active" : "inactive"}
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    <button className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs font-semibold text-gray-700" onClick={() => startEdit(row)}>
                      Edit
                    </button>
                    <button
                      className="rounded-lg border border-teal-200 bg-teal-50 px-2 py-1.5 text-xs font-semibold text-teal-700"
                      onClick={() =>
                        setCreateForm({
                          branch_id: row.branch_id,
                          full_name: `${row.full_name} (copy)`,
                          skill_tags_csv: row.skill_tags.join(","),
                          is_active: true,
                        })
                      }
                    >
                      Clone
                    </button>
                    <button
                      className="rounded-lg border border-rose-200 bg-rose-50 px-2 py-1.5 text-xs font-semibold text-rose-700"
                      onClick={() => deleteMutation.mutate(row.id)}
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}
        {!staff.isLoading && filtered.length === 0 && (
          <div className="rounded-xl border border-dashed border-gray-300 p-5 text-center text-sm text-gray-500">
            No staff for current filter.
          </div>
        )}
      </div>
    </div>
  );
}

function StaffFormFields({
  form,
  branches,
  onChange,
}: {
  form: StaffForm;
  branches: Branch[];
  onChange: Dispatch<SetStateAction<StaffForm>>;
}) {
  return (
    <div className="grid gap-2">
      <select
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        value={form.branch_id}
        onChange={(e) => onChange((prev) => ({ ...prev, branch_id: e.target.value }))}
      >
        <option value="">Select branch</option>
        {branches.map((b) => (
          <option key={b.id} value={b.id}>
            {b.name}
          </option>
        ))}
      </select>
      <input
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        value={form.full_name}
        placeholder="Full name"
        onChange={(e) => onChange((prev) => ({ ...prev, full_name: e.target.value }))}
      />
      <input
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        value={form.skill_tags_csv}
        placeholder="cuts,color"
        onChange={(e) => onChange((prev) => ({ ...prev, skill_tags_csv: e.target.value }))}
      />
    </div>
  );
}
